import { useState } from "react"

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import { DescriptionRounded } from "@mui/icons-material"

import DataControl from "./DataControl"
import Accordion from "./Accordion"

const CopywritingItem = ({ name, data, dataController, isLast }) => {
  const [notesOpen, setNotesOpen] = useState(false)

  const openNotes = () => setNotesOpen(true)
  const closeNotes = () => setNotesOpen(false)

  const notes = dataController.get(`${name}.notes`)

  return (
    <Box>
      <Dialog open={notesOpen} maxWidth="sm" fullWidth onClose={closeNotes}>
        <DialogTitle>Edit Notes</DialogTitle>

        <DialogContent>
          <Box pt={2}>
            <DataControl
              component={TextField}
              label="Notes"
              multiline
              minRows={4}
              name={`${name}.notes`}
              controller={dataController}
              permissionType="edit_notes"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: 3 }}>
          <Button onClick={closeNotes}>Close</Button>
        </DialogActions>
      </Dialog>

      <Stack direction="row" justifyContent="space-between" p={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <DataControl
            component="switch"
            size="small"
            label="Include?"
            name={`${name}.enabled`}
            controller={dataController}
            permissionType="edit_details"
          />

          <DataControl
            component={TextField}
            label="Name"
            name={`${name}.label`}
            controller={dataController}
            permissionType="edit_details"
            size="small"
            sx={{ width: 180 }}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <DataControl
            component={TextField}
            type="number"
            inputProps={{ min: 1 }}
            label="Pages"
            name={`${name}.pages`}
            controller={dataController}
            permissionType="edit_estimates"
            required={data.enabled}
            size="small"
            sx={{ width: 80, opacity: data.enabled ? 1 : 0.3 }}
          />

          <DataControl
            component={TextField}
            type="number"
            inputProps={{ min: 1 }}
            label="Words/Page"
            name={`${name}.words`}
            controller={dataController}
            permissionType="edit_estimates"
            required={data.enabled}
            size="small"
            sx={{ width: 100, opacity: data.enabled ? 1 : 0.3 }}
          />

          <DataControl
            component={TextField}
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            label="Rate per Word"
            name={`${name}.ratePerWord`}
            controller={dataController}
            permissionType="edit_estimates"
            required={data.enabled}
            size="small"
            sx={{ width: 140, opacity: data.enabled ? 1 : 0.3 }}
          />

          <IconButton onClick={openNotes} sx={{ opacity: notes ? 1 : 0.3 }}>
            {notes ? (
              <Tooltip
                title={
                  <Typography variant="caption">
                    {notes
                      .trim()
                      .split("\n")
                      .map((l, x) => (
                        <span key={x}>
                          {l}
                          <br />
                        </span>
                      ))}
                  </Typography>
                }
              >
                <DescriptionRounded sx={{ width: 18, height: 18 }} />
              </Tooltip>
            ) : (
              <DescriptionRounded sx={{ width: 18, height: 18 }} />
            )}
          </IconButton>
        </Stack>
      </Stack>

      {!isLast && <Divider />}
    </Box>
  )
}

const CopywritingSection = ({ name, data, dataController }) => {
  const baseName = `data.scope.additionalOptions.${name}.copywritingItems`

  // Handle both array (new) and object (legacy) data structures
  const copywritingItems = data.copywritingItems || []
  const itemsArray = Array.isArray(copywritingItems)
    ? copywritingItems
    : Object.values(copywritingItems)

  // Calculate total cost for copywriting items
  const totalCost = itemsArray.reduce((total, item) => {
    if (!item || !item.enabled) return total
    const pages = Number(item.pages) || 1
    const words = Number(item.words) || 0
    const ratePerWord = Number(item.ratePerWord) || 0.5
    return total + pages * words * ratePerWord
  }, 0)

  const totalWords = itemsArray.reduce((total, item) => {
    if (!item || !item.enabled) return total
    const pages = Number(item.pages) || 1
    const words = Number(item.words) || 0
    return total + pages * words
  }, 0)

  const formattedCost = totalCost.toLocaleString("en", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return (
    <Box>
      <Accordion
        title={data.label}
        elevation={2}
        defaultCollapsed
        disablePadding
        AdditionalInfo={
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              variant="outlined"
              label={`${totalWords} words`}
            />
            <Chip size="small" label={formattedCost} />
          </Stack>
        }
      >
        <Box>
          {itemsArray.map((item, i) => {
            // For legacy object structure, we need to find the original key
            const itemKey = Array.isArray(copywritingItems)
              ? i
              : Object.keys(copywritingItems)[i] || i

            return (
              <CopywritingItem
                key={i}
                name={`${baseName}.${itemKey}`}
                data={item}
                dataController={dataController}
                isLast={i === itemsArray.length - 1}
              />
            )
          })}
        </Box>
      </Accordion>
    </Box>
  )
}

export default CopywritingSection
